import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'
import { UserCircle } from '@phosphor-icons/react'
import { saveAccessToken } from '@/lib/token-storage'
import { API_BASE_URL } from '@/config/api'
import { z } from 'zod'
import { AuthLayout } from '@/components/auth/auth-layout'

const importSessionSearchSchema = z.object({
  access_token: z.string().min(20).optional(),
  then: z.string().optional(),
})

export const Route = createFileRoute('/auth/import-session')({
  validateSearch: importSessionSearchSchema,
  component: AuthImportSessionPage,
})

function AuthImportSessionPage() {
  const search = useSearch({ from: '/auth/import-session' })
  const navigate = useNavigate()
  const applied = useRef(false)

  useEffect(() => {
    if (applied.current) return
    applied.current = true

    const bootstrapRefreshCookie = async () => {
      const base = API_BASE_URL ? API_BASE_URL.replace(/\/$/, '') : ''
      const url = search.access_token
        ? `${base}/api/v2/auth/bootstrap-refresh-cookie`
        : `${base}/api/v2/auth/refresh-token`
      try {
        const headers: Record<string, string> = {
          Accept: 'application/json',
        }
        if (search.access_token) {
          headers.Authorization = `Bearer ${search.access_token}`
        }

        const response = await fetch(url, {
          method: 'POST',
          headers,
          credentials: 'include',
        })

        if (!search.access_token && response.ok) {
          const contentType = response.headers.get('content-type')
          if (contentType?.includes('application/json')) {
            const data = (await response.json()) as { accessToken?: string }
            if (data?.accessToken) {
              saveAccessToken(data.accessToken)
            }
          }
        }
      } catch {
        // Mantém fallback com access token local; sem cookie a sessão pode exigir novo login ao expirar.
      }
    }

    const applySession = async () => {
      if (search.access_token) {
        saveAccessToken(search.access_token)
      }
      await bootstrapRefreshCookie()
      const raw = search.then?.trim()
      const next =
        raw && raw.startsWith('/') && !raw.startsWith('//') ? raw : '/conta'
      navigate({ to: next, replace: true })
    }

    void applySession()
  }, [navigate, search.access_token, search.then])

  return (
    <AuthLayout contentMaxWidthClassName="max-w-sm">
      <div className="flex flex-col items-center gap-6 py-2 text-center">
        <div
          className="relative flex size-20 items-center justify-center"
          aria-hidden
        >
          <span className="absolute inset-0 rounded-full border-2 border-indigo-100 bg-indigo-50/80" />
          <span className="absolute inset-0 animate-[spin_1.1s_linear_infinite] rounded-full border-2 border-transparent border-t-indigo-600 border-r-indigo-400/40" />
          <UserCircle
            weight="duotone"
            className="relative z-[1] size-9 text-indigo-600 animate-pulse"
          />
        </div>
        <div className="space-y-2 animate-auth-item-enter">
          <h1 className="font-heading text-lg font-semibold tracking-tight text-slate-900">
            Acessando sua conta
          </h1>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Estamos preparando tudo para você continuar com segurança.
          </p>
          <div className="flex justify-center gap-2 pt-2" aria-hidden>
            {[0, 160, 320].map((delay) => (
              <span
                key={delay}
                className="size-2 rounded-full bg-indigo-500/75 animate-pulse"
                style={{ animationDelay: `${delay}ms` }}
              />
            ))}
          </div>
        </div>
      </div>
    </AuthLayout>
  )
}
